// src/pages/services/Blockchain.jsx - Premium Web3 Solutions
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowLeft, ArrowRight, Shield, Coins, Network, Code, Zap, Lock, Layers, BarChart3 } from 'lucide-react';
import RelatedServices from '../../components/RelatedServices';

export default function Blockchain() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const services = [
    {
      icon: <Coins className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Smart Contract Development',
      description: 'Audited smart contracts for DeFi, NFTs, and DAOs',
      features: ['Solidity Development', 'Security Audits', 'Gas Optimization', 'Full Testing']
    },
    {
      icon: <Network className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'DApp Development',
      description: 'Full-stack decentralized applications with Web3',
      features: ['Smart Contract Integration', 'Wallet Connection', 'IPFS Integration', 'Frontend Dev']
    },
    {
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'NFT Solutions',
      description: 'NFT marketplaces and minting platforms',
      features: ['ERC-721/1155 Contracts', 'Marketplace Design', 'Royalty Systems', 'Metadata Management']
    },
    {
      icon: <Lock className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'DeFi Solutions',
      description: 'Decentralized finance protocols and platforms',
      features: ['Token Creation', 'Staking Pools', 'Yield Farming', 'Liquidity Management']
    }
  ];

  const blockchains = [
    { name: 'Ethereum', icon: '⟠', color: 'from-blue-500 to-purple-500' },
    { name: 'BSC', icon: '🔶', color: 'from-yellow-500 to-orange-500' },
    { name: 'Polygon', icon: '🟣', color: 'from-purple-500 to-pink-500' },
    { name: 'Solana', icon: '◎', color: 'from-teal-500 to-blue-500' },
    { name: 'Avalanche', icon: '🔺', color: 'from-red-500 to-pink-500' },
    { name: 'Arbitrum', icon: '🔵', color: 'from-blue-500 to-cyan-500' }
  ];

  const technologies = [
    { name: 'Solidity', icon: '📜' },
    { name: 'Rust', icon: '🦀' },
    { name: 'Web3.js', icon: '🌐' },
    { name: 'Ethers.js', icon: '⚡' },
    { name: 'Hardhat', icon: '⛏️' },
    { name: 'Truffle', icon: '🍫' }
  ];

  const pricingPlans = [
    {
      name: 'Smart Contract',
      price: 15999,
      monthlySupport: 499,
      color: 'amber',
      badge: null,
      features: [
        'Basic Smart Contract Development',
        'Solidity Implementation',
        'Testnet Testing & Deployment',
        '2 Months Support Included',
        'Contract Documentation',
        'Gas Optimization'
      ]
    },
    {
      name: 'DApp Development',
      price: 49999,
      monthlySupport: 1999,
      color: 'orange',
      badge: 'MOST POPULAR',
      featured: true,
      features: [
        'Full-Stack DApp Build',
        'Custom Smart Contracts',
        'Web3 Frontend Integration',
        '4 Months Support Included',
        'Security Audit Included',
        'Production Deployment',
        'Wallet & IPFS Integration'
      ]
    },
    {
      name: 'Enterprise Web3',
      price: null,
      monthlySupport: 7500,
      color: 'red',
      badge: null,
      features: [
        'Custom Web3 Solutions',
        'Dedicated Development Team',
        '24/7 Priority Support',
        'Advanced Security Audits',
        'Multi-Chain Deployment',
        'Ongoing Optimization',
        'Scalability Architecture'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-amber-600 via-orange-600 to-red-600 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center gap-2 text-white hover:underline mb-4 sm:mb-6 text-xs sm:text-base font-medium">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" /> Back to Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 leading-tight">
                Blockchain & Web3 Development
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-amber-100 mb-6 sm:mb-8 leading-relaxed">
                Build the future with smart contracts, DApps, and decentralized solutions. Expert development across all major blockchains.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/contact" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-white text-amber-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base shadow-lg">
                  Start Web3 Project
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-sm sm:text-base">
                  View Blockchain Projects
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1639322537504-6427a16b0a28?w=600&h=400&fit=crop"
                alt="Blockchain Development"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Our Web3 Services
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              Complete blockchain development from concept to mainnet deployment
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center text-amber-600 mb-3 sm:mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">{service.description}</p>
                <ul className="space-y-1.5 sm:space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Web3 Solutions Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-amber-600/10 via-orange-600/10 to-red-600/10 px-2 sm:px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl p-6 sm:p-12 shadow-2xl border border-amber-200/50 dark:border-amber-800/50">
            <Layers className="w-16 h-16 sm:w-24 sm:h-24 text-amber-600 mx-auto mb-4 sm:mb-8 opacity-75 animate-pulse" />
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-4 sm:mb-6 text-center">
              Build Your Web3 Solution
            </h2>
            <p className="text-base sm:text-xl text-gray-700 dark:text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed text-center px-2">
              We design and implement blockchain solutions tailored to your specific needs. 
              From NFT marketplaces to DeFi protocols and DAO governance systems - 
              <strong className="font-semibold text-amber-600 dark:text-amber-400"> we build secure and scalable Web3 applications</strong>. 
              Let us help you bring your decentralized vision to life.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mb-6 sm:mb-12">
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 rounded-2xl border border-amber-200/50 hover:shadow-2xl transition-all duration-300">
                <Coins className="w-12 h-12 sm:w-16 sm:h-16 text-amber-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">NFT Marketplace</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-amber-600">Digital Asset Management</div>
                  <div className="text-base sm:text-lg text-orange-600">Royalty Systems</div>
                </div>
              </div>
              
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 rounded-2xl border border-amber-200/50 hover:shadow-2xl transition-all duration-300">
                <Zap className="w-12 h-12 sm:w-16 sm:h-16 text-amber-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">DeFi Protocol</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-amber-600">Liquidity Pools</div>
                  <div className="text-base sm:text-lg text-orange-600">Yield Farming</div>
                </div>
              </div>
              
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 rounded-2xl border border-amber-200/50 hover:shadow-2xl transition-all duration-300">
                <Network className="w-12 h-12 sm:w-16 sm:h-16 text-amber-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">DAO Governance</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-amber-600">Community Governance</div>
                  <div className="text-base sm:text-lg text-orange-600">Voting Systems</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:gap-6">
              <Link to="/contact" className="px-6 sm:px-12 py-3 sm:py-5 bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 text-white rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 transition-all duration-300 text-center tracking-wide">
                🚀 Start Your Project
              </Link>
              <Link to="/portfolio" className="px-6 sm:px-12 py-3 sm:py-5 border-2 sm:border-4 border-amber-600 text-amber-600 dark:text-amber-400 rounded-2xl font-black text-base sm:text-xl hover:bg-amber-600 hover:text-white transition-all duration-300 text-center tracking-wide">
                View Our Projects
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Blockchains */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Supported Blockchains
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-2">
              We develop on all major blockchain networks
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
            {blockchains.map((blockchain, index) => (
              <div key={index} className="group">
                <div className={`bg-gradient-to-br ${blockchain.color} p-4 sm:p-6 rounded-xl shadow-lg text-center hover:scale-110 hover:shadow-2xl transition-all duration-300 cursor-pointer border border-white/20`}>
                  <div className="text-3xl sm:text-5xl mb-2 sm:mb-3 text-white">{blockchain.icon}</div>
                  <h3 className="font-bold text-white text-xs sm:text-sm tracking-wide">{blockchain.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Technologies & Tools
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 p-3 sm:p-6 rounded-xl shadow-lg text-center hover:scale-105 transition-all border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-3xl sm:text-5xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform">{tech.icon}</div>
                <h3 className="font-bold text-xs sm:text-base text-gray-900 dark:text-white">{tech.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Our Blockchain Development Process
          </h2>
          <div className="grid md:grid-cols-5 gap-3 sm:gap-6">
            {[
              { step: '1', title: 'Discovery', desc: 'Define Web3 requirements' },
              { step: '2', title: 'Architecture', desc: 'Design blockchain solution' },
              { step: '3', title: 'Development', desc: 'Build smart contracts' },
              { step: '4', title: 'Security Audit', desc: 'Comprehensive testing' },
              { step: '5', title: 'Mainnet Deploy', desc: 'Go live & monitor' }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-amber-600 to-orange-600 text-white rounded-full flex items-center justify-center text-lg sm:text-2xl font-black mx-auto mb-2 sm:mb-4 shadow-lg group-hover:scale-110 transition-transform">
                  {item.step}
                </div>
                <h3 className="text-sm sm:text-lg font-bold mb-1 sm:mb-2 text-gray-900 dark:text-white">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-3 sm:mb-4">
              Web3 Development Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2 leading-relaxed">
              Transparent pricing for blockchain solutions. From smart contracts to enterprise DApps.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-20">
            {pricingPlans.map((plan, idx) => {
              const colorMap = {
                amber: { bg: 'from-amber-600 to-orange-600', light: 'from-amber-50 to-orange-50', hover: 'hover:border-amber-200' },
                orange: { bg: 'from-orange-600 to-red-600', light: 'from-orange-50 to-red-50', hover: 'hover:border-orange-200' },
                red: { bg: 'from-red-600 to-rose-600', light: 'from-red-50 to-rose-50', hover: 'hover:border-red-200' }
              };
              const colors = colorMap[plan.color];

              return (
                <div
                  key={idx}
                  className={`group relative ${plan.featured ? 'md:scale-105' : ''} bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col ${colors.hover} dark:hover:border-gray-600/50 ${plan.featured ? 'border-2 border-orange-500 dark:border-orange-600' : ''}`}
                >
                  {/* Glow effect for featured */}
                  {plan.featured && (
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-red-500/10 dark:from-orange-600/20 dark:to-red-600/20 rounded-2xl opacity-100 -z-10 blur-xl"></div>
                  )}

                  {/* Badge */}
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`bg-gradient-to-r ${colors.bg} text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg`}>
                        {plan.badge} ⭐
                      </div>
                    </div>
                  )}

                  <h3 className={`text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2 ${plan.featured ? 'mt-4 sm:mt-6' : ''}`}>{plan.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                    {plan.name === 'Smart Contract' && 'Perfect for simple dApps'}
                    {plan.name === 'DApp Development' && 'Best for scalable platforms'}
                    {plan.name === 'Enterprise Web3' && 'Custom solutions at scale'}
                  </p>

                  {/* Price */}
                  <div className="mb-8">
                    {plan.price ? (
                      <>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'amber' ? '#b45309, #ea580c' : plan.color === 'orange' ? '#ea580c, #dc2626' : '#dc2626, #e11d48'})`}}>
                            ${plan.price.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'amber' ? '#b45309, #ea580c' : plan.color === 'orange' ? '#ea580c, #dc2626' : '#dc2626, #e11d48'})`}}>
                            ${plan.monthlySupport.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                        </div>
                      </>
                    ) : (
                      <div>
                        <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'amber' ? '#b45309, #ea580c' : plan.color === 'orange' ? '#ea580c, #dc2626' : '#dc2626, #e11d48'})`}}>
                          ${plan.monthlySupport.toLocaleString()}
                        </div>
                        <span className="text-gray-600 dark:text-gray-400 font-semibold">/month retainer</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Custom scoping available</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} style={{backgroundColor: `${plan.color === 'amber' ? '#fed7aa' : plan.color === 'orange' ? '#fed7aa' : '#fecaca'}`}}>
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 font-bold" style={{color: `${plan.color === 'amber' ? '#b45309' : plan.color === 'orange' ? '#ea580c' : '#dc2626'}`}} />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link
                    to="/contact"
                    className={`w-full px-6 py-3 sm:py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base transform hover:-translate-y-1 text-center ${
                      plan.featured
                        ? `bg-gradient-to-r ${colors.bg} text-white hover:shadow-2xl`
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                  >
                    {plan.name === 'Enterprise Web3' ? 'Let\'s Talk' : 'Get Started'}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* All Plans Include */}
          <div className="max-w-5xl mx-auto mb-12 sm:mb-16">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-8">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: '🔒', text: 'Security Audit' },
                { icon: '📚', text: 'Full Documentation' },
                { icon: '⚙️', text: 'Gas Optimization' },
                { icon: '📊', text: 'Performance Analysis' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl border border-amber-200/50 dark:border-amber-800/50 p-6 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Web3 Development FAQ</h3>
            <div className="space-y-4 sm:space-y-5">
              {[
                {
                  q: 'How long does a Web3 project take?',
                  a: 'Smart contracts: 2-4 weeks. DApp development: 3-5 months. Enterprise solutions: Custom timeline. We provide detailed timelines during discovery.'
                },
                {
                  q: 'What blockchain should I choose?',
                  a: 'We recommend based on your use case. Ethereum for maximum compatibility, Polygon for cost-efficiency, Solana for speed. Let\'s discuss your needs.'
                },
                {
                  q: 'Is security auditing necessary?',
                  a: 'Yes! All production smart contracts need auditing. We include comprehensive audits to identify and fix vulnerabilities before mainnet launch.'
                },
                {
                  q: 'Can you integrate with existing systems?',
                  a: 'Absolutely! We can connect your blockchain solution to existing databases, APIs, and systems using secure bridges and oracles.'
                },
                {
                  q: 'What about regulatory compliance?',
                  a: 'We help with compliance strategies, KYC/AML implementation, and documentation to meet legal requirements in your jurisdiction.'
                }
              ].map((faq, idx) => (
                <details key={idx} className="group cursor-pointer">
                  <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span className="text-sm sm:text-base text-left pr-4">{faq.q}</span>
                    <span className="group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
                  </summary>
                  <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 text-white px-2 sm:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Layers className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 animate-pulse" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6">
            Ready to Build on Blockchain?
          </h2>
          <p className="text-base sm:text-xl text-amber-100 mb-6 sm:mb-8 px-2">
            Join the Web3 revolution. Let's create your decentralized future with cutting-edge blockchain solutions.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 sm:px-10 py-3 sm:py-5 bg-white text-amber-600 rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300"
          >
            <span>🚀 Start Your Blockchain Project</span>
            <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </Link>
        </div>
      </section>
      <div className="mt-16">
      <RelatedServices currentService="blockchain" />
      </div>
    </div>
  );
}